import axios from "axios";
import {
  LoginCredentials,
  AuthResponse,
  CreateUserDto,
  TwoFactorVerification,
  Organization,
  User,
  Participant,
  Test,
  TestDeletionInfo,
  TestParticipant,
  PaginatedResponse,
  CreateOrganizationDto,
  CreateOrganizationWithUserDto,
  CreateOrganizationWithUserResponse,
  CreateParticipantDto,
  UpdateParticipantDto,
  ParticipantClassHistoryEntry,
  UpdateUserDto,
  UpdateOwnProfileDto,
  UserSelectOption,
  CreateUserForOrganizationDto,
  TwoFactorSetupResponse,
  UpdateAttendanceDto,
  TestAttendanceResponse,
  SaveTestResultsDto,
  TestResult,
  UpdateTestResultDto,
  UpdateTestParticipantDto,
  CalculateResultsDto,
  CalculateResultsResponse,
  SdqQuestionnaireState,
  SdqProgressDto,
  SdqSubmitDto,
  SdqResult,
  ActiveIntervention,
  AdminIntervention,
  CreateInterventionDto,
  UpdateInterventionDto,
  TestInterventionAssignment,
  UpdateTestInterventionsDto,
  AdminDataQuery,
  AdminDataResponse,
  AdminDataFilterOptions,
  ChartFilters,
  ChartGroupQuery,
  ChartMapQuery,
  ChartMapResponse,
  ChartMapDrilldownQuery,
  ChartMapDrilldownResponse,
  ChartPdfQuery,
  ChartGroupResponse,
  ChartGroupOptions,
  ChartParticipantResponse,
} from "@autfit/shared-types";
import { toAdminDataSearch, withoutPaging } from "../utils/adminDataQuery";
import { createRefreshCoordinator, recordFreshSession } from "./sessionRefresh";
import { emitSessionExpired } from "./sessionEvents";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include cookies in requests
});

declare module "axios" {
  interface AxiosRequestConfig {
    /** Set on the retry after a refresh, so a request is retried once. */
    _retry?: boolean;
    /** Never start a refresh for this request (the /auth/me probe). */
    _skipAuthRefresh?: boolean;
  }
}

// Auth endpoints whose 401 means "wrong credentials", not "token expired".
const NO_REFRESH_PATHS = [
  "/auth/refresh",
  "/auth/login",
  "/auth/verify-2fa",
  "/auth/register",
];

// One refresh at a time per browser, shared by the interceptor, the
// interval/visibility hook and manual refreshes (sessionRefresh.ts).
const refreshCoordinator = createRefreshCoordinator({
  refresh: () =>
    api.post("/auth/refresh", {}, { timeout: 20000 }).then(() => undefined),
  probe: () =>
    api
      .get("/auth/me", { _skipAuthRefresh: true, timeout: 20000 })
      .then(() => true),
  onExpired: emitSessionExpired,
});

/** Refreshes the session (deduplicated in this tab, locked across tabs). */
export const refreshSession = (): Promise<void> => refreshCoordinator.refresh();

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url: string = originalRequest?.url ?? "";

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest._skipAuthRefresh &&
      !NO_REFRESH_PATHS.some((path) => url.includes(path))
    ) {
      originalRequest._retry = true;
      // Rejects with SessionExpiredError (re-login dialog is shown) or a
      // network error; the original request is then not retried.
      await refreshSession();
      return api(originalRequest);
    }

    return Promise.reject(error);
  },
);

const recordIfLoggedIn = (response: AuthResponse): AuthResponse => {
  if (!response.requiresTwoFactor) recordFreshSession();
  return response;
};

export const authAPI = {
  login: (credentials: LoginCredentials): Promise<AuthResponse> =>
    api
      .post("/auth/login", credentials)
      .then((res) => recordIfLoggedIn(res.data)),

  register: (userData: CreateUserDto): Promise<AuthResponse> =>
    api.post("/auth/register", userData).then((res) => res.data),

  verify2FA: (
    userId: string,
    verification: TwoFactorVerification,
  ): Promise<AuthResponse> =>
    api
      .post(`/auth/verify-2fa/${userId}`, verification)
      .then((res) => recordIfLoggedIn(res.data)),

  setup2FA: (): Promise<TwoFactorSetupResponse> =>
    api.post("/auth/setup-2fa").then((res) => res.data),

  logout: (): Promise<void> => api.post("/auth/logout").then(() => {}),

  logoutAll: (): Promise<void> => api.post("/auth/logout-all").then(() => {}),

  getMe: (): Promise<User> => api.get("/auth/me").then((res) => res.data),

  /** Coordinated refresh; never sends a second request for the same cookie. */
  refreshToken: (): Promise<void> => refreshSession(),
};

export const organizationsAPI = {
  getAll: (page = 1, pageSize = 10): Promise<PaginatedResponse<Organization>> =>
    api
      .get(`/organizations?page=${page}&pageSize=${pageSize}`)
      .then((res) => res.data),

  getById: (id: string): Promise<Organization> =>
    api.get(`/organizations/${id}`).then((res) => res.data),

  create: (data: CreateOrganizationDto): Promise<Organization> =>
    api.post("/organizations", data).then((res) => res.data),

  createWithUser: (
    data: CreateOrganizationWithUserDto,
  ): Promise<CreateOrganizationWithUserResponse> =>
    api.post("/organizations/with-user", data).then((res) => res.data),

  update: (id: string, data: CreateOrganizationDto): Promise<Organization> =>
    api.patch(`/organizations/${id}`, data).then((res) => res.data),

  delete: (id: string): Promise<void> => api.delete(`/organizations/${id}`),

  getUsers: (
    id: string,
    page = 1,
    pageSize = 10,
  ): Promise<PaginatedResponse<User>> =>
    api
      .get(`/organizations/${id}/users?page=${page}&pageSize=${pageSize}`)
      .then((res) => res.data),

  /** Super admin only (SuperAdminGuard); the school comes from the URL. */
  createUser: (
    orgId: string,
    data: CreateUserForOrganizationDto,
  ): Promise<User> =>
    api.post(`/organizations/${orgId}/users`, data).then((res) => res.data),

  // ==================== SUPERADMIN USER MANAGEMENT ====================

  updateUser: (
    orgId: string,
    userId: string,
    data: UpdateUserDto,
  ): Promise<User> =>
    api
      .patch(`/organizations/${orgId}/users/${userId}`, data)
      .then((res) => res.data),

  deleteUser: (orgId: string, userId: string): Promise<void> =>
    api.delete(`/organizations/${orgId}/users/${userId}`),

  // ==================== SUPERADMIN PARTICIPANT MANAGEMENT ====================

  getParticipants: (
    orgId: string,
    page = 1,
    pageSize = 10,
    search?: string,
    className?: string,
  ): Promise<PaginatedResponse<Participant>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append("search", search);
    if (className) params.append("className", className);

    return api
      .get(`/organizations/${orgId}/participants?${params.toString()}`)
      .then((res) => res.data);
  },

  createParticipant: (
    orgId: string,
    data: CreateParticipantDto,
  ): Promise<Participant> =>
    api
      .post(`/organizations/${orgId}/participants`, data)
      .then((res) => res.data),

  updateParticipant: (
    orgId: string,
    participantId: string,
    data: UpdateParticipantDto,
  ): Promise<Participant> =>
    api
      .patch(`/organizations/${orgId}/participants/${participantId}`, data)
      .then((res) => res.data),

  deleteParticipant: (orgId: string, participantId: string): Promise<void> =>
    api.delete(`/organizations/${orgId}/participants/${participantId}`),

  getParticipantClasses: (orgId: string): Promise<string[]> =>
    api
      .get(`/organizations/${orgId}/participants/classes`)
      .then((res) => res.data),

  // ==================== SUPERADMIN TEST MANAGEMENT ====================

  getTests: (
    orgId: string,
    page = 1,
    pageSize = 10,
    search?: string,
  ): Promise<PaginatedResponse<Test>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append("search", search);

    return api
      .get(`/organizations/${orgId}/tests?${params.toString()}`)
      .then((res) => res.data);
  },

  createTest: (orgId: string, data: any): Promise<Test> =>
    api.post(`/organizations/${orgId}/tests`, data).then((res) => res.data),

  getTest: (orgId: string, testId: string): Promise<Test> =>
    api.get(`/organizations/${orgId}/tests/${testId}`).then((res) => res.data),

  updateTest: (orgId: string, testId: string, data: any): Promise<Test> =>
    api
      .patch(`/organizations/${orgId}/tests/${testId}`, data)
      .then((res) => res.data),

  deleteTest: (orgId: string, testId: string): Promise<void> =>
    api.delete(`/organizations/${orgId}/tests/${testId}`),

  startTest: (orgId: string, testId: string): Promise<Test> =>
    api
      .post(`/organizations/${orgId}/tests/${testId}/start`)
      .then((res) => res.data),

  completeTest: (orgId: string, testId: string): Promise<Test> =>
    api
      .post(`/organizations/${orgId}/tests/${testId}/complete`)
      .then((res) => res.data),

  getTestAttendance: (
    orgId: string,
    testId: string,
  ): Promise<TestAttendanceResponse> =>
    api
      .get(`/organizations/${orgId}/tests/${testId}/attendance`)
      .then((res) => res.data),

  updateTestAttendance: (
    orgId: string,
    testId: string,
    data: UpdateAttendanceDto,
  ): Promise<TestAttendanceResponse> =>
    api
      .put(`/organizations/${orgId}/tests/${testId}/attendance`, data)
      .then((res) => res.data),

  batchUpdateTestAttendance: (
    orgId: string,
    testId: string,
    data: { participantIds: string[]; attendance: string },
  ): Promise<TestAttendanceResponse> =>
    api
      .put(`/organizations/${orgId}/tests/${testId}/attendance/batch`, data)
      .then((res) => res.data),

  addTestParticipant: (
    orgId: string,
    testId: string,
    participantId: string,
  ): Promise<Test> =>
    api
      .post(`/organizations/${orgId}/tests/${testId}/participants`, {
        participantId,
      })
      .then((res) => res.data),

  saveTestResults: (
    orgId: string,
    testId: string,
    data: SaveTestResultsDto,
  ): Promise<TestResult[]> =>
    api
      .post(`/organizations/${orgId}/tests/${testId}/results`, data)
      .then((res) => res.data),

  getTestResults: (orgId: string, testId: string): Promise<any> =>
    api
      .get(`/organizations/${orgId}/tests/${testId}/results`)
      .then((res) => res.data),

  getTestResultsMinimal: (orgId: string, testId: string): Promise<any> =>
    api
      .get(`/organizations/${orgId}/tests/${testId}/results/minimal`)
      .then((res) => res.data),

  getParticipantResults: (
    orgId: string,
    testId: string,
    participantId: string,
  ): Promise<TestResult[]> =>
    api
      .get(`/organizations/${orgId}/tests/${testId}/results/${participantId}`)
      .then((res) => res.data),

  updateTestResult: (
    orgId: string,
    resultId: string,
    data: UpdateTestResultDto,
  ): Promise<TestResult> =>
    api
      .patch(`/organizations/${orgId}/tests/results/${resultId}`, data)
      .then((res) => res.data),

  deleteTestResult: (orgId: string, resultId: string): Promise<void> =>
    api.delete(`/organizations/${orgId}/tests/results/${resultId}`),

  deleteParticipantTaskResults: (
    orgId: string,
    testId: string,
    testParticipantId: string,
    taskId: string,
  ): Promise<void> =>
    api.delete(
      `/organizations/${orgId}/tests/${testId}/participants/${testParticipantId}/tasks/${taskId}/results`,
    ),

  addTestInstructor: (
    orgId: string,
    testId: string,
    userId: string,
  ): Promise<Test> =>
    api
      .post(`/organizations/${orgId}/tests/${testId}/instructors`, { userId })
      .then((res) => res.data),

  removeTestInstructor: (
    orgId: string,
    testId: string,
    userId: string,
  ): Promise<Test> =>
    api
      .delete(`/organizations/${orgId}/tests/${testId}/instructors/${userId}`)
      .then((res) => res.data),

  getTestInstructors: (orgId: string, testId: string): Promise<User[]> =>
    api
      .get(`/organizations/${orgId}/tests/${testId}/instructors`)
      .then((res) => res.data),
};

export const usersAPI = {
  getAll: (
    page = 1,
    pageSize = 10,
    search?: string,
    role?: string,
  ): Promise<PaginatedResponse<User>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append("search", search);
    if (role) params.append("role", role);

    return api.get(`/users?${params.toString()}`).then((res) => res.data);
  },

  getById: (id: string): Promise<User> =>
    api.get(`/users/${id}`).then((res) => res.data),

  create: (data: CreateUserDto): Promise<User> =>
    api.post("/users", data).then((res) => res.data),

  update: (id: string, data: UpdateUserDto): Promise<User> =>
    api.patch(`/users/${id}`, data).then((res) => res.data),

  delete: (id: string): Promise<void> => api.delete(`/users/${id}`),

  /** Own profile (Settings), every logged-in user. */
  updateMe: (data: UpdateOwnProfileDto): Promise<User> =>
    api.patch("/users/me", data).then((res) => res.data),

  /** Active colleagues of the own school (instructor dialog). */
  getForSelect: (): Promise<UserSelectOption[]> =>
    api.get("/users/select").then((res) => res.data),
};

export const participantsAPI = {
  getAll: (
    page = 1,
    pageSize = 10,
    search?: string,
    className?: string,
  ): Promise<PaginatedResponse<Participant>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append("search", search);
    if (className) params.append("className", className);

    return api
      .get(`/participants?${params.toString()}`)
      .then((res) => res.data);
  },

  getById: (id: string): Promise<Participant> =>
    api.get(`/participants/${id}`).then((res) => res.data),

  getClassHistory: (id: string): Promise<ParticipantClassHistoryEntry[]> =>
    api.get(`/participants/${id}/class-history`).then((res) => res.data),

  create: (data: CreateParticipantDto): Promise<Participant> =>
    api.post("/participants", data).then((res) => res.data),

  update: (id: string, data: UpdateParticipantDto): Promise<Participant> =>
    api.patch(`/participants/${id}`, data).then((res) => res.data),

  delete: (id: string): Promise<void> => api.delete(`/participants/${id}`),

  getUniqueClasses: (): Promise<string[]> =>
    api.get("/participants/classes").then((res) => res.data),

  checkExistingSSNs: (ssnList: string[]): Promise<string[]> =>
    api
      .post("/participants/check-existing-ssns", { ssnList })
      .then((res) => res.data),
};

export const interventionsAPI = {
  // Active interventions (any authenticated user)
  getActive: (): Promise<ActiveIntervention[]> =>
    api.get("/interventions").then((res) => res.data),

  // Global catalogue (SUPER_ADMIN only)
  getAllAdmin: (): Promise<AdminIntervention[]> =>
    api.get("/admin/interventions").then((res) => res.data),

  create: (data: CreateInterventionDto): Promise<AdminIntervention> =>
    api.post("/admin/interventions", data).then((res) => res.data),

  update: (
    id: string,
    data: UpdateInterventionDto,
  ): Promise<AdminIntervention> =>
    api.patch(`/admin/interventions/${id}`, data).then((res) => res.data),
};

// Superadmin cross-school data view (pseudonymised)
export const adminDataAPI = {
  list: (query: AdminDataQuery): Promise<AdminDataResponse> =>
    api
      .get(`/admin/data?${toAdminDataSearch(query).toString()}`)
      .then((res) => res.data),

  getFilterOptions: (): Promise<AdminDataFilterOptions> =>
    api.get("/admin/data/filter-options").then((res) => res.data),

  export: (query: AdminDataQuery, format: "csv" | "xlsx"): Promise<Blob> => {
    const params = toAdminDataSearch(withoutPaging(query));
    params.set("format", format);
    return api
      .get(`/admin/data/export?${params.toString()}`, { responseType: "blob" })
      .then((res) => res.data);
  },
};

// Diagramme (group means per school, individual progress per child)
/** Filters as repeated query params; gender / sportsClub only when set. */
const chartParams = (
  query: Partial<ChartFilters> & { organizationIds?: string[] },
  extra: Record<string, string | undefined> = {},
) => {
  const params = new URLSearchParams();
  Object.entries(extra).forEach(([k, v]) => v && params.set(k, v));
  query.organizationIds?.forEach((id) => params.append("organizationIds", id));
  query.ageGroups?.forEach((a) => params.append("ageGroups", String(a)));
  query.schoolLevels?.forEach((s) => params.append("schoolLevels", String(s)));
  query.classNames?.forEach((c) => params.append("classNames", c));
  if (query.gender) params.set("gender", query.gender);
  if (query.sportsClub) params.set("sportsClub", query.sportsClub);
  query.weightClasses?.forEach((w) => params.append("weightClasses", w));
  query.birthYears?.forEach((y) => params.append("birthYears", String(y)));
  return params;
};

export const chartsAPI = {
  getGroup: (query: ChartGroupQuery): Promise<ChartGroupResponse> =>
    api
      .get(
        `/charts/group?${chartParams(query, {
          metric: query.metric,
          compare: query.compare ?? undefined,
          compare2: query.compare2 ?? undefined,
        })}`,
      )
      .then((res) => res.data),

  getGroupOptions: (
    organizationIds?: string[],
    filters: Pick<ChartFilters, "gender" | "sportsClub"> = {},
  ): Promise<ChartGroupOptions> =>
    api
      .get(
        `/charts/group/options?${chartParams({ organizationIds, ...filters })}`,
      )
      .then((res) => res.data),

  /** Map of Austria by Bundesland (super admins; selected schools or all). */
  getMap: (query: ChartMapQuery): Promise<ChartMapResponse> =>
    api
      .get(
        `/charts/map?${chartParams(query, {
          metric: query.metric,
          measure: query.measure,
          feature: query.feature,
          weightShare: query.weightShare,
          timepoint: query.timepoint,
          compare: query.compare ?? undefined,
          compare2: query.compare2 ?? undefined,
        })}`,
      )
      .then((res) => res.data),

  /** Map drill-down: Bezirke of a Bundesland or schools of a Bezirk. */
  getMapDrilldown: (
    query: ChartMapDrilldownQuery,
  ): Promise<ChartMapDrilldownResponse> =>
    api
      .get(
        `/charts/map/drilldown?${chartParams(query, {
          metric: query.metric,
          measure: query.measure,
          feature: query.feature,
          weightShare: query.weightShare,
          timepoint: query.timepoint,
          state: query.state,
          district: query.district,
          compare: query.compare ?? undefined,
          compare2: query.compare2 ?? undefined,
        })}`,
      )
      .then((res) => res.data),

  /** Dashboard PDF (A4 landscape) of the current view, rendered by the API. */
  downloadPdf: (query: ChartPdfQuery): Promise<Blob> =>
    api
      .get(
        `/charts/pdf?${chartParams(query, {
          view: query.view,
          mode: query.view === "chart" ? query.mode : undefined,
          compare: query.compare ?? undefined,
          compare2: query.compare2 ?? undefined,
          metric: query.metric,
          timepoint: query.timepoint,
          measure: query.view === "map" ? query.measure : undefined,
          feature: query.view === "map" ? query.feature : undefined,
          weightShare: query.view === "map" ? query.weightShare : undefined,
          development:
            query.view === "chart" && query.development ? "true" : undefined,
        })}`,
        { responseType: "blob" },
      )
      .then((res) => res.data),

  getParticipant: (participantId: string): Promise<ChartParticipantResponse> =>
    api
      .get(`/charts/participant/${encodeURIComponent(participantId)}`)
      .then((res) => res.data),
};

export const testAPI = {
  getTests: (
    page = 1,
    pageSize = 10,
    search?: string,
  ): Promise<PaginatedResponse<Test>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.append("search", search);

    return api.get(`/tests?${params.toString()}`).then((res) => res.data);
  },

  getById: (id: string): Promise<Test> =>
    api.get(`/tests/${id}`).then((res) => res.data),

  create: (data: any): Promise<Test> =>
    api.post("/tests", data).then((res) => res.data),

  update: (id: string, data: any): Promise<Test> =>
    api.patch(`/tests/${id}`, data).then((res) => res.data),

  delete: (id: string): Promise<void> => api.delete(`/tests/${id}`),

  // Soft delete confirmation data (SUPER_ADMIN or ADMINISTRATIVE of the school)
  getDeletionInfo: (id: string): Promise<TestDeletionInfo> =>
    api.get(`/tests/${id}/deletion-info`).then((res) => res.data),

  startTest: (id: string): Promise<Test> =>
    api.post(`/tests/${id}/start`).then((res) => res.data),

  completeTest: (id: string): Promise<Test> =>
    api.post(`/tests/${id}/complete`).then((res) => res.data),

  getAttendance: (id: string): Promise<TestAttendanceResponse> =>
    api.get(`/tests/${id}/attendance`).then((res) => res.data),

  updateAttendance: (
    id: string,
    data: UpdateAttendanceDto,
  ): Promise<TestAttendanceResponse> =>
    api.put(`/tests/${id}/attendance`, data).then((res) => res.data),

  batchUpdateAttendance: (
    id: string,
    data: { participantIds: string[]; attendance: string },
  ): Promise<TestAttendanceResponse> =>
    api.put(`/tests/${id}/attendance/batch`, data).then((res) => res.data),

  addParticipant: (id: string, participantId: string): Promise<Test> =>
    api
      .post(`/tests/${id}/participants`, { participantId })
      .then((res) => res.data),

  // Interventions per test participation
  getInterventions: (id: string): Promise<TestInterventionAssignment[]> =>
    api.get(`/tests/${id}/interventions`).then((res) => res.data),

  updateInterventions: (
    id: string,
    data: UpdateTestInterventionsDto,
  ): Promise<TestInterventionAssignment[]> =>
    api.put(`/tests/${id}/interventions`, data).then((res) => res.data),

  // Test Results API
  saveTestResults: (
    testId: string,
    data: SaveTestResultsDto,
  ): Promise<TestResult[]> =>
    api.post(`/tests/${testId}/results`, data).then((res) => res.data),

  getTestResults: (testId: string): Promise<any> =>
    api.get(`/tests/${testId}/results`).then((res) => res.data),

  getTestResultsMinimal: (testId: string): Promise<any> =>
    api.get(`/tests/${testId}/results/minimal`).then((res) => res.data),

  getParticipantResults: (
    testId: string,
    participantId: string,
  ): Promise<TestResult[]> =>
    api
      .get(`/tests/${testId}/results/${participantId}`)
      .then((res) => res.data),

  updateTestResult: (
    resultId: string,
    data: UpdateTestResultDto,
  ): Promise<TestResult> =>
    api.patch(`/tests/results/${resultId}`, data).then((res) => res.data),

  deleteTestResult: (resultId: string): Promise<void> =>
    api.delete(`/tests/results/${resultId}`),

  deleteParticipantTaskResults: (
    testId: string,
    testParticipantId: string,
    taskId: string,
  ): Promise<void> =>
    api.delete(
      `/tests/${testId}/participants/${testParticipantId}/tasks/${taskId}/results`,
    ),

  // PDF Export API
  exportParticipantPdf: (
    testId: string,
    participantId: string,
  ): Promise<Blob> =>
    api
      .get(`/tests/${testId}/pdf/participants/${participantId}`, {
        responseType: "blob",
      })
      .then((res) => res.data),

  exportBulkPdf: (testId: string, participantIds?: string[]): Promise<Blob> => {
    const params =
      participantIds && participantIds.length > 0
        ? `?participantIds=${participantIds.join(",")}`
        : "";
    return api
      .get(`/tests/${testId}/pdf/bulk${params}`, {
        responseType: "blob",
      })
      .then((res) => res.data);
  },

  // Excel Export API
  exportTestResultsExcel: (testId: string): Promise<Blob> =>
    api
      .get(`/tests/${testId}/results/export/excel`, {
        responseType: "blob",
      })
      .then((res) => res.data),

  // --- Mentale Gesundheit (SDQ) ---

  // Ensure the stable one-time link/token for a test participant.
  ensureSdqToken: (
    testId: string,
    testParticipantId: string,
  ): Promise<{ token: string; publicUrl: string }> =>
    api
      .post(`/tests/${testId}/sdq/participants/${testParticipantId}/token`)
      .then((res) => res.data),

  // Pre-create the in-software run (records conductChannel = IN_SOFTWARE).
  startSdqInSoftware: (
    testId: string,
    testParticipantId: string,
  ): Promise<{ token: string; publicUrl: string }> =>
    api
      .post(
        `/tests/${testId}/sdq/participants/${testParticipantId}/start-in-software`,
      )
      .then((res) => res.data),

  getSdqQr: (
    testId: string,
    testParticipantId: string,
  ): Promise<{ token: string; publicUrl: string; qrDataUrl: string }> =>
    api
      .get(`/tests/${testId}/sdq/participants/${testParticipantId}/qr`)
      .then((res) => res.data),

  // Single id -> one QR page; multiple -> one combined PDF.
  getSdqQrPdf: (testId: string, testParticipantIds: string[]): Promise<Blob> =>
    api
      .get(`/tests/${testId}/sdq/qr.pdf`, {
        params: { testParticipantIds: testParticipantIds.join(",") },
        responseType: "blob",
      })
      .then((res) => res.data),

  // Recommendations PDF
  getRecommendationsAvailability: (
    testId: string,
    participantId: string,
  ): Promise<{
    available: boolean;
    missingTasks: string[];
    missingDerivedCalculations: ("BMI" | "WtHR")[];
  }> =>
    api
      .get(
        `/tests/${testId}/recommendations/participants/${participantId}/availability`,
      )
      .then((res) => res.data),

  exportRecommendationsPdf: (
    testId: string,
    participantId: string,
    environment: "HOUSE_GARDEN" | "APARTMENT_PARK",
  ): Promise<Blob> =>
    api
      .get(
        `/tests/${testId}/recommendations/participants/${participantId}/pdf`,
        {
          params: { environment },
          responseType: "blob",
        },
      )
      .then((res) => res.data),

  // Nutrition recommendations PDF
  getNutritionAvailability: (
    testId: string,
    participantId: string,
  ): Promise<{
    available: boolean;
    missingTasks: string[];
    missingDerivedCalculations: ("BMI" | "WtHR")[];
  }> =>
    api
      .get(
        `/tests/${testId}/recommendations/participants/${participantId}/nutrition/availability`,
      )
      .then((res) => res.data),

  exportNutritionPdf: (testId: string, participantId: string): Promise<Blob> =>
    api
      .get(
        `/tests/${testId}/recommendations/participants/${participantId}/nutrition/pdf`,
        {
          responseType: "blob",
        },
      )
      .then((res) => res.data),

  // Instructor Management API
  addInstructor: (testId: string, userId: string): Promise<Test> =>
    api
      .post(`/tests/${testId}/instructors`, { userId })
      .then((res) => res.data),

  removeInstructor: (testId: string, userId: string): Promise<Test> =>
    api
      .delete(`/tests/${testId}/instructors/${userId}`)
      .then((res) => res.data),

  getInstructors: (testId: string): Promise<User[]> =>
    api.get(`/tests/${testId}/instructors`).then((res) => res.data),

  // Test Participant API
  updateTestParticipant: (
    testId: string,
    testParticipantId: string,
    data: UpdateTestParticipantDto,
  ): Promise<TestParticipant> =>
    api
      .patch(`/tests/${testId}/participants/${testParticipantId}`, data)
      .then((res) => res.data),
};

export const calculationsAPI = {
  /**
   * Calculate z-scores and STA9 for all participants in a test
   */
  calculateForTest: (
    testId: string,
    participantIds?: string[],
  ): Promise<CalculateResultsResponse> =>
    api
      .post("/calculations/calculate", { testId, participantIds })
      .then((res) => res.data),

  /**
   * Calculate z-score and STA9 for a single test result
   */
  calculateForResult: (
    resultId: string,
  ): Promise<{ success: boolean; warnings: any[] }> =>
    api
      .post(`/calculations/calculate/result/${resultId}`)
      .then((res) => res.data),

  /**
   * Delete a calculated result (for recalculation)
   */
  deleteCalculatedResult: (calculatedResultId: string): Promise<void> =>
    api.delete(`/calculations/${calculatedResultId}`),

  /**
   * Plausibility bounds for every (participant, task) in a test.
   * Used to flag mistyped measurement entries (>3σ from age/gender mean)
   * without blocking submission.
   */
  getPlausibilityBounds: (
    testId: string,
    options?: { taskId?: string; ballWeight?: number; equipmentType?: string },
  ): Promise<{
    bounds: Array<{
      participantId: string;
      testParticipantId: string;
      taskId: string;
      mean: number;
      s: number;
      lower: number;
      upper: number;
    }>;
  }> => {
    const params: Record<string, string | number> = {};
    if (options?.taskId) params.taskId = options.taskId;
    if (options?.ballWeight !== undefined)
      params.ballWeight = options.ballWeight;
    if (options?.equipmentType) params.equipmentType = options.equipmentType;
    return api
      .get(`/calculations/plausibility/${testId}`, { params })
      .then((res) => res.data);
  },
};

// Alias for backward compatibility
export const testsApi = testAPI;

// --- Public (unauthenticated) SDQ questionnaire client ---
// Separate axios instance: NO credentials, NO 401-refresh interceptor, so an
// anonymous participant never triggers an /auth/refresh loop.
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

export const publicQuestionnaireApi = {
  getState: (token: string): Promise<SdqQuestionnaireState> =>
    publicApi.get(`/public/sdq/${token}`).then((res) => res.data),

  saveProgress: (token: string, dto: SdqProgressDto): Promise<{ ok: true }> =>
    publicApi
      .patch(`/public/sdq/${token}/progress`, dto)
      .then((res) => res.data),

  submit: (token: string, dto: SdqSubmitDto): Promise<SdqResult> =>
    publicApi.post(`/public/sdq/${token}/submit`, dto).then((res) => res.data),
};

export default api;
